import { useState } from "react";
import { FiCheck, FiGrid, FiList, FiPlus, FiTrash2, FiFolderPlus } from "react-icons/fi";
import { MdOutlineDriveFolderUpload, MdUploadFile } from "react-icons/md";
import { FaArrowDownWideShort } from "react-icons/fa6";
import { usePopperTooltip } from "react-popper-tooltip";
import Dropdown from "../dropdowns/dropdown";

type BrowseActionsProps = {
  isCurrentFolderRestricted: boolean;
  selectedItemsCount: number;
  onDeleteSelected: () => void;
  onNewFolder: () => void;
  onUploadFile: () => void;
  onUploadFolder: () => void;
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  sortBy: "name" | "size" | "modified" | "none";
  sortOrder: "asc" | "desc";
  onSelectSortBy: (value: "name" | "size" | "modified" | "none") => void;
  onSelectSortOrder: (value: "asc" | "desc") => void;
};

const BrowseActions = ({
  isCurrentFolderRestricted,
  selectedItemsCount,
  onDeleteSelected,
  onNewFolder,
  onUploadFile,
  onUploadFolder,
  viewMode,
  onViewModeChange,
  sortBy,
  sortOrder,
  onSelectSortBy,
  onSelectSortOrder,
}: BrowseActionsProps) => {
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortOptions: Array<BrowseActionsProps["sortBy"]> = [
    "none",
    "name",
    "size",
    "modified",
  ];
  const sortOrderOptions: Array<BrowseActionsProps["sortOrder"]> = ["asc", "desc"];
  const closeOnSortBy: Record<BrowseActionsProps["sortBy"], boolean> = {
    none: true,
    name: false,
    size: false,
    modified: false,
  };
  const closeOnSortOrder: Record<BrowseActionsProps["sortOrder"], boolean> = {
    asc: true,
    desc: true,
  };
  const {
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible,
  } = usePopperTooltip({ placement: "bottom-end" });

  return (
    <div className="flex items-center space-x-3">
      <div className="h-6 w-px bg-gray-300" />
      {selectedItemsCount > 0 && (
        <>
          {/* TODO: remove hidden class when delete functionality is implemented end-to-end. */}
          <button
            onClick={onDeleteSelected}
            className="hidden px-2.5 py-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-all items-center gap-1.5"
            title={`Delete ${selectedItemsCount} selected item${
              selectedItemsCount > 1 ? "s" : ""
            }`}
          >
            <FiTrash2 size={15} />
            <span className="text-sm">{selectedItemsCount}</span>
          </button>
          <div className="hidden h-6 w-px bg-gray-300" />
        </>
      )}
      {/* TODO: remove hidden class when addendum create workflow is implemented end-to-end. */}
      <div className="hidden relative">
        <div className="relative" ref={setTriggerRef}>
          <Dropdown
            label={
              <span className="flex items-center space-x-1">
                <FiPlus size={16} />
                <span className="text-sm">Create</span>
              </span>
            }
            isOpen={showNewMenu}
            setIsOpen={setShowNewMenu}
            showCaret={false}
            buttonDisabled={isCurrentFolderRestricted}
            buttonStyle={`rounded-lg border-0 items-center ${
              isCurrentFolderRestricted
                ? "bg-gray-400 opacity-50 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 cursor-pointer text-white"
            }`}
            menuClassName="right-0 left-auto origin-top-right min-w-[180px]"
          >
            <button
              onClick={() => {
                onNewFolder();
                setShowNewMenu(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-left transition-colors rounded-t-lg hover:bg-gray-100"
            >
              <FiFolderPlus size={16} className="text-gray-500" />
              <span>New folder</span>
            </button>
            <button
              onClick={() => {
                onUploadFile();
                setShowNewMenu(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-left transition-colors hover:bg-gray-100"
            >
              <MdUploadFile size={16} className="text-gray-500" />
              <span>Upload file</span>
            </button>
            <button
              onClick={() => {
                onUploadFolder();
                setShowNewMenu(false);
              }}
              className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-left transition-colors rounded-b-lg hover:bg-gray-50"
            >
              <MdOutlineDriveFolderUpload size={16} className="text-gray-500" />
              <span>Upload folder</span>
            </button>
          </Dropdown>
        </div>
        {isCurrentFolderRestricted && visible && (
          <div
            ref={setTooltipRef}
            {...getTooltipProps({
              className:
                "mt-2 px-2 py-1 text-xs rounded whitespace-nowrap z-[9999] border bg-gray-800 text-gray-100 border-gray-600 pointer-events-none",
            })}
          >
            <p>This directory is read only. See addendum for file uploads</p>
          </div>
        )}
      </div>
      <div className="flex rounded-lg border p-0.5 bg-gray-100 border-gray-300">
        <button
          onClick={() => onViewModeChange("grid")}
          className={`px-2.5 py-1 rounded transition-all ${
            viewMode === "grid"
              ? "bg-blue-600 text-white"
              : "text-gray-600 hover:text-gray-800"
          }`}
          title="Grid view"
        >
          <FiGrid size={15} />
        </button>
        <button
          onClick={() => onViewModeChange("list")}
          className={`px-2.5 py-1 rounded transition-all ${
            viewMode === "list"
              ? "bg-blue-600 text-white"
              : "text-gray-600 hover:text-gray-800"
          }`}
          title="List view"
        >
          <FiList size={15} />
        </button>
      </div>
      <div className="h-6 w-px bg-gray-300" />
      <div className="relative">
        <Dropdown
          label={
            <span className="flex items-center space-x-1">
              <FaArrowDownWideShort size={16} />
              <span className="text-sm">Sort by</span>
            </span>
          }
          isOpen={showSortMenu}
          setIsOpen={setShowSortMenu}
          closeOnScroll
          buttonStyle="border-0 p-2 text-gray-700 hover:bg-gray-100 items-center"
          menuClassName="right-0 left-auto origin-top-right min-w-[160px]"
        >
          <div className="p-2">
            <div className="text-xs font-semibold mb-2 text-gray-600">
              Sort by
            </div>
            {sortOptions.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onSelectSortBy(option);
                  if (closeOnSortBy[option]) {
                    setShowSortMenu(false);
                  }
                }}
                className={`w-full flex items-center px-2 py-1 text-sm rounded ${
                  sortBy === option
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100"
                }`}
              >
                <div className="w-4 mr-1">
                  {sortBy === option && <FiCheck size={12} />}
                </div>
                <span>
                  {option === "modified"
                    ? "Modified Date"
                    : option.charAt(0).toUpperCase() + option.slice(1)}
                </span>
              </button>
            ))}
            {sortBy !== "none" && (
              <>
                <div className="text-xs font-semibold mt-3 mb-2 text-gray-600">
                  Order
                </div>
                {sortOrderOptions.map((order) => (
                  <button
                    key={order}
                    onClick={() => {
                      onSelectSortOrder(order);
                      if (closeOnSortOrder[order]) {
                        setShowSortMenu(false);
                      }
                    }}
                    className={`w-full flex items-center px-2 py-1 text-sm rounded ${
                      sortOrder === order
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <div className="w-4 mr-1">
                      {sortOrder === order && <FiCheck size={12} />}
                    </div>
                    <span>{order === "asc" ? "Ascending" : "Descending"}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};

export default BrowseActions;
